import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/api/client";
import { Loader2 } from "lucide-react";

const SEM_COLOR = { rojo: "bg-rose-500", amarillo: "bg-amber-400", verde: "bg-emerald-500" };

export default function AdminDiagnosticSummary() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/dashboard/diagnostic-summary").then((r) => setData(r.data)).catch((e) => setError(apiErrorMessage(e)));
  }, []);

  if (error) return <div className="text-rose-600 text-sm font-semibold">{error}</div>;
  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-brand-accent" />;

  if (data.n === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-black text-slate-800">Resumen del Diagnóstico</h1>
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-sm text-slate-500">{data.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-diagnostic-summary">
      <div>
        <h1 className="text-2xl font-black text-slate-800">Resumen del Diagnóstico</h1>
        <p className="text-sm text-slate-500">Basado en {data.n_completed} diagnósticos completados (los incompletos se excluyen de los promedios).</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <span className="text-[10px] font-bold text-slate-400 uppercase">Promedio IHD</span>
          <div className="text-4xl font-black text-brand-slate mt-1">{data.avg_ihd}</div>
          <div className="flex gap-2 mt-3 text-[10px] font-bold">
            {["rojo","amarillo","verde"].map((s) => <span key={s} className={`px-2 py-1 rounded-md text-white ${SEM_COLOR[s]}`}>{s}: {data.ihd_semaphore_pct[s]}%</span>)}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <span className="text-[10px] font-bold text-slate-400 uppercase">Promedio Casos (sobre 24)</span>
          <div className="text-4xl font-black text-brand-accent mt-1">{data.avg_cases_total_24}</div>
          <p className="text-[10px] text-slate-400 mt-2">Puntaje 0 en un caso es una respuesta válida, no ausencia de respuesta.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-800 mb-4">Resultados por dimensión (D1-D6)</h3>
        <div className="space-y-3">
          {data.dimensions.map((d) => (
            <div key={d.id} className="flex items-center gap-3">
              <span className="w-8 text-xs font-black text-slate-600">{d.id}</span>
              <div className="flex-1 flex h-6 rounded-lg overflow-hidden bg-slate-100">
                {["rojo","amarillo","verde"].map((s) => (
                  <div key={s} className={SEM_COLOR[s]} style={{ width: `${d.semaphore_pct[s]}%` }} title={`${s}: ${d.semaphore_pct[s]}%`} />
                ))}
              </div>
              <span className="w-16 text-xs font-bold text-slate-700 text-right">{d.avg_score ?? "-"} / 100</span>
              <span className="w-12 text-[10px] text-slate-400 text-right">n={d.n}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-sm font-black text-slate-800 mb-4">Distribución de alternativas por caso (0-3)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {Object.entries(data.case_option_distribution).map(([cid, dist]) => (
            <div key={cid} className="bg-slate-50 rounded-xl p-3">
              <p className="text-[10px] font-bold text-slate-500 mb-2">{cid}</p>
              {["0","1","2","3"].map((pt) => (
                <div key={pt} className="flex items-center gap-2 text-[10px] mb-1">
                  <span className="w-3 font-bold text-slate-400">{pt}</span>
                  <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-brand-accent" style={{ width: `${(dist[pt] / data.n_completed) * 100}%` }} /></div>
                  <span className="text-slate-500 w-6 text-right">{dist[pt]}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
